import { ArrowDown, ArrowUp, Copy, Lock, MoreHorizontal, Pencil, Pin, PinOff, Share2, Trash2 } from 'lucide-react'
import { useId, useRef, useState, type FormEvent } from 'react'
import { useMe } from '@/auth/useMe'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/lib/toast'
import { toastUndo } from '@/lib/toastUndo'
import { createView, deleteView, restoreView, setPins, updateView } from '../query/api'
import type { CrmObject, ViewDef, ViewsResponse } from '../query/types'
import { DEFAULT_VIEW } from '../query/urlState'

const MAX_PINS = 8
const MAX_NAME = 60
const COPY_SUFFIX = ' (kopie)'
const LIMIT_TEXT = `Maximaal ${MAX_PINS} vastgepinde weergaven`

type TabKey = 'standaard' | 'mijn' | 'team'

const message = (caught: unknown): string => (caught instanceof Error ? caught.message : 'Er ging iets mis.')

/** The pins as the screen shows them (`shown`, changed at once), and as the server last confirmed them. */
interface PinState {
  shown: string[]
  confirmed: string[]
  /** The `pins` of the data these came from: a new one replaces both. */
  source: string[]
}

function pinStateOf(pins: string[]): PinState {
  return { shown: pins, confirmed: pins, source: pins }
}

interface RowProps {
  view: ViewDef
  current: boolean
  pinned: boolean
  /** All the pins are taken, and this view is not one of them. */
  pinBlocked: boolean
  renaming: boolean
  onOpen: () => void
  onTogglePin: () => void
  onDuplicate: () => void
  onStartRename: () => void
  onRename: (name: string) => Promise<void>
  onCancelRename: () => void
  onShare: () => void
  onRemove: () => void
}

function ViewRow(props: RowProps) {
  const { view, current, pinned, pinBlocked, renaming } = props
  const saved = view.id !== null
  const nameFieldRef = useRef<HTMLInputElement>(null)
  // Set when "Hernoemen" is chosen: the menu then leaves the focus to the name field, not its button.
  const renameChosenRef = useRef(false)
  const [draft, setDraft] = useState(view.name)
  const [wasRenaming, setWasRenaming] = useState(renaming)
  if (renaming !== wasRenaming) {
    setWasRenaming(renaming)
    if (renaming) setDraft(view.name)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    const name = draft.trim()
    if (name && name !== view.name) await props.onRename(name)
    else props.onCancelRename()
  }

  return (
    <li className="flex flex-col gap-1 border-b border-border py-2 last:border-b-0" aria-current={current ? 'true' : undefined}>
      <div className="flex items-center gap-1">
        <div className="min-w-0 flex-1">
          <span className="font-medium break-words">{view.name}</span>
          {view.visibility === 'team' ? (
            <span className="ml-2 text-sm text-muted-foreground">Team{view.owner ? ` · ${view.owner.full_name}` : ''}</span>
          ) : null}
          {current ? <span className="ml-2 text-sm text-muted-foreground">Geopend</span> : null}
        </div>
        <Button type="button" variant="outline" size="sm" aria-label={`${view.name} openen`} onClick={props.onOpen}>
          Openen
        </Button>
        {saved ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={pinned ? `${view.name} losmaken` : `${view.name} vastpinnen`}
            disabled={pinBlocked}
            title={pinBlocked ? LIMIT_TEXT : undefined}
            onClick={props.onTogglePin}
          >
            {pinned ? <PinOff /> : <Pin />}
          </Button>
        ) : null}
        {saved ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon-sm" aria-label={`Acties voor ${view.name}`}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onCloseAutoFocus={(event) => {
                if (!renameChosenRef.current) return
                renameChosenRef.current = false
                event.preventDefault()
                nameFieldRef.current?.focus()
              }}
            >
              <DropdownMenuItem onSelect={props.onDuplicate}>
                <Copy className="size-3.5" aria-hidden />
                Dupliceren
              </DropdownMenuItem>
              {view.can_edit ? (
                <>
                  <DropdownMenuItem
                    onSelect={() => {
                      renameChosenRef.current = true
                      props.onStartRename()
                    }}
                  >
                    <Pencil className="size-3.5" aria-hidden />
                    Hernoemen
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={props.onShare}>
                    {view.visibility === 'team' ? <Lock className="size-3.5" aria-hidden /> : <Share2 className="size-3.5" aria-hidden />}
                    {view.visibility === 'team' ? 'Privé maken' : 'Delen met team'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={props.onRemove}>
                    <Trash2 className="size-3.5" aria-hidden />
                    Verwijderen
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      {view.description ? <p className="text-sm text-muted-foreground">{view.description}</p> : null}
      {renaming ? (
        <form onSubmit={(event) => void submit(event)} className="flex items-center gap-2">
          <Input
            ref={nameFieldRef}
            aria-label={`Nieuwe naam voor ${view.name}`}
            maxLength={MAX_NAME}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            className="max-md:h-11 max-md:text-base"
          />
          <Button type="submit" size="sm" disabled={!draft.trim()}>
            Bewaren
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={props.onCancelRename}>
            Annuleren
          </Button>
        </form>
      ) : null}
    </li>
  )
}

/**
 * "Alle weergaven" (design §5.4): every view of an object in three tabs (Standaard, Mijn, Team) with
 * a search field. Saved views can be opened, pinned as a tab and duplicated by anyone; the maker
 * (and an admin, for a team view) can also rename, share, make private and delete them, with
 * "Ongedaan maken" after a delete. The pinned views come first, in the order of their tabs, with
 * ↑/↓ to change it (dragging needs a new dependency). At most 8 can be pinned.
 *
 * "Mijn" holds the views the user made, "Team" the team views of colleagues. That goes by who made
 * a view, not by who may change it: an admin may change every team view (`can_edit`), and a
 * colleague's view does not become theirs for that. Every change calls `onChanged`, so the page
 * reloads the views.
 *
 * A delete closes the dialog before its toast shows: a modal dialog leaves nothing outside it
 * clickable, so the first click on "Ongedaan maken" would only close the dialog. Deleting the open
 * view also opens the standard view, so the page does not find its own view gone (and say "Deze
 * weergave bestaat niet meer." next to the undo); "Ongedaan maken" opens it again once the page has
 * read the views back.
 */
export function AllViewsDialog({
  open,
  onOpenChange,
  object,
  data,
  currentKey,
  onOpen,
  onChanged,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  object: CrmObject
  data: ViewsResponse
  /** The `key` of the open view. */
  currentKey: string
  /** Opens a view (its `key`): one chosen in the dialog, the standard view when the open one is deleted, and that one again on its undo. */
  onOpen: (key: string) => void
  /** Reads the views again. A promise says when they are in; `false` means they could not be read. */
  onChanged: () => void | Promise<boolean>
}) {
  const pinnedHeading = useId()
  const { me } = useMe()
  const [tab, setTab] = useState<TabKey>('standaard')
  const [search, setSearch] = useState('')
  const [renaming, setRenaming] = useState<string | null>(null)
  const [pins, setPinState] = useState<PinState>(() => pinStateOf(data.pins))
  const writes = useRef(0)
  if (pins.source !== data.pins) setPinState(pinStateOf(data.pins))
  const [wasOpen, setWasOpen] = useState(open)
  // Every opening starts at the standard views, without the search of the last visit hiding any.
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setTab('standaard')
      setSearch('')
      setRenaming(null)
    }
  }

  const byKey = new Map(data.views.map((view) => [view.key, view]))
  const pinned = pins.shown.flatMap((id) => byKey.get(id) ?? [])
  const full = pins.shown.length >= MAX_PINS
  const needle = search.trim().toLowerCase()
  const matches = (view: ViewDef): boolean => view.name.toLowerCase().includes(needle)
  // Private views are always the user's own (the server shows no one else's). Without a user (the profile is
  // still loading, or failed) no team view can be shown to be theirs.
  const ofColleague = (view: ViewDef): boolean => view.visibility === 'team' && (!me || view.owner?.id !== me.id)
  const lists: Record<TabKey, ViewDef[]> = {
    standaard: data.system.filter(matches),
    mijn: data.views.filter((view) => !ofColleague(view)).filter(matches),
    team: data.views.filter(ofColleague).filter(matches),
  }
  const empty: Record<TabKey, string> = {
    standaard: 'Geen weergaven gevonden.',
    mijn: needle ? 'Geen weergaven gevonden.' : 'Je hebt nog geen eigen weergaven. Pas de filters aan en kies "Opslaan als nieuwe weergave".',
    team: needle ? 'Geen weergaven gevonden.' : 'Het team heeft nog geen weergaven gedeeld.',
  }

  /** Runs a change; a failure shows its message and the dialog stays as it is. */
  async function run(change: () => Promise<void>): Promise<void> {
    try {
      await change()
    } catch (caught) {
      toast.error(message(caught))
    }
  }

  /** Pins the views in this order. It shows at once; the answer of the newest write is the one that counts. */
  async function writePins(next: string[]): Promise<void> {
    writes.current += 1
    const mine = writes.current
    setPinState((state) => ({ ...state, shown: next }))
    try {
      const result = await setPins(object, next)
      if (mine !== writes.current) return
      setPinState((state) => ({ ...state, shown: result.pins, confirmed: result.pins }))
      onChanged()
    } catch (caught) {
      if (mine === writes.current) setPinState((state) => ({ ...state, shown: state.confirmed }))
      toast.error(message(caught))
    }
  }

  function movePin(index: number, step: number) {
    const keys = pinned.map((view) => view.key)
    const target = index + step
    if (target < 0 || target >= keys.length) return
    const next = [...keys]
    next[index] = keys[target]
    next[target] = keys[index]
    void writePins(next)
  }

  function openView(view: ViewDef) {
    onOpen(view.key)
    onOpenChange(false)
  }

  function duplicate(view: ViewDef) {
    const name = view.name.length + COPY_SUFFIX.length > MAX_NAME ? view.name.slice(0, MAX_NAME - COPY_SUFFIX.length) : view.name
    return run(async () => {
      await createView({ object_type: object, name: `${name}${COPY_SUFFIX}`, visibility: 'prive', query: view.query, columns: view.columns, sort: view.sort })
      onChanged()
    })
  }

  function rename(view: ViewDef, name: string) {
    return run(async () => {
      await updateView(view.key, { name })
      setRenaming(null)
      onChanged()
    })
  }

  function share(view: ViewDef) {
    return run(async () => {
      await updateView(view.key, { visibility: view.visibility === 'team' ? 'prive' : 'team' })
      onChanged()
    })
  }

  function remove(view: ViewDef) {
    const wasOpen = view.key === currentKey
    return run(async () => {
      await deleteView(view.key)
      // Closed first: while the modal dialog is open, a click on the toast's "Ongedaan maken" only closes it.
      onOpenChange(false)
      toastUndo(`Weergave '${view.name}' verwijderd`, async () => {
        await restoreView(view.key)
        // Opening it before the page has it back would be opening a view that does not exist.
        const readBack = (await onChanged()) !== false
        if (wasOpen && readBack) onOpen(view.key)
      })
      if (wasOpen) onOpen(DEFAULT_VIEW)
      onChanged()
    })
  }

  function togglePin(view: ViewDef) {
    const keys = pins.shown
    void writePins(keys.includes(view.key) ? keys.filter((key) => key !== view.key) : [...keys, view.key])
  }

  const row = (view: ViewDef) => {
    const isPinned = pins.shown.includes(view.key)
    return (
      <ViewRow
        key={view.key}
        view={view}
        current={view.key === currentKey}
        pinned={isPinned}
        pinBlocked={full && !isPinned}
        renaming={renaming === view.key}
        onOpen={() => openView(view)}
        onTogglePin={() => togglePin(view)}
        onDuplicate={() => void duplicate(view)}
        onStartRename={() => setRenaming(view.key)}
        onRename={(name) => rename(view, name)}
        onCancelRename={() => setRenaming(null)}
        onShare={() => void share(view)}
        onRemove={() => void remove(view)}
      />
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Alle weergaven</DialogTitle>
        </DialogHeader>
        <div className="mt-4 flex flex-col gap-4">
          <Input
            type="search"
            aria-label="Zoek weergave"
            placeholder="Zoek weergave…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="max-md:h-11 max-md:text-base"
          />
          {pinned.length > 0 ? (
            <section aria-labelledby={pinnedHeading} className="flex flex-col gap-1">
              <h3 id={pinnedHeading} className="text-sm font-semibold">
                Vastgepind
              </h3>
              <ul className="flex flex-col">
                {pinned.map((view, index) => (
                  <li key={view.key} className="flex items-center gap-1">
                    <span className="min-w-0 flex-1 truncate text-sm">{view.name}</span>
                    <Button type="button" variant="ghost" size="icon-sm" aria-label={`${view.name} omhoog`} disabled={index === 0} onClick={() => movePin(index, -1)}>
                      <ArrowUp />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`${view.name} omlaag`}
                      disabled={index === pinned.length - 1}
                      onClick={() => movePin(index, 1)}
                    >
                      <ArrowDown />
                    </Button>
                    <Button type="button" variant="ghost" size="sm" aria-label={`Losmaken: ${view.name}`} onClick={() => togglePin(view)}>
                      Losmaken
                    </Button>
                  </li>
                ))}
              </ul>
              {full ? <p className="text-sm text-muted-foreground">{LIMIT_TEXT} bereikt.</p> : null}
            </section>
          ) : null}
          <Tabs value={tab} onValueChange={(value) => setTab(value as TabKey)}>
            <TabsList>
              {(
                [
                  ['standaard', 'Standaard'],
                  ['mijn', 'Mijn'],
                  ['team', 'Team'],
                ] as const
              ).map(([key, label]) => (
                // A click selects too, next to Radix's mouse-down: assistive technology often sends only a click.
                <TabsTrigger key={key} value={key} onClick={() => setTab(key)}>
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
            {(['standaard', 'mijn', 'team'] as const).map((key) => (
              <TabsContent key={key} value={key} className="mt-2">
                {lists[key].length > 0 ? <ul className="flex flex-col">{lists[key].map(row)}</ul> : <p className="py-4 text-sm text-muted-foreground">{empty[key]}</p>}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  )
}
